/**
 * Draws SET.Live light show looks on a 2D canvas (ENG-2291), shared by the fan
 * app (the takeover), the editor (look previews) and Backstage (thumbnails).
 * Browser-only: it uses canvas, Path2D, Image and fetch.
 */
import {
  ColorShiftLook,
  CustomLookId,
  LightShowLook,
  LightShowSpeed,
  PatternId,
  PatternLook,
  PatternMotion,
  SolidLook,
} from ".";
import {
  BOLT_PATH,
  CHEVRON_PATH,
  COMP_WIDTH,
  CONFETTI_TILE_SIZE,
  HEART_PATH,
  STAR_PATH,
  WAVE_A,
  WAVE_B,
} from "./shapes";
import { CUSTOM_LOOKS } from "./custom";
import {
  getAnimationStillTime,
  isAnimationReady,
  preloadAnimation,
  renderAnimation,
} from "./animation";

export { setCassetteLoader } from "./custom/homeFreeCassette";
export {
  checkAnimationFile,
  MAX_ANIMATION_BYTES,
  setLottieLoader,
} from "./animation";
export type { AnimationFileCheck } from "./animation";

/**
 * Photosensitivity guard: nothing may flash more than 3 times per second
 * (WCAG 2.3.1). Every motion below is derived from rates under this limit.
 */
export const MAX_FLASH_HZ = 3;

type Ctx = CanvasRenderingContext2D;

/**
 * Pattern speed as color alternations per second at any point on screen, so
 * every pattern stays under MAX_FLASH_HZ no matter how dense it is.
 */
const PATTERN_HZ: Record<LightShowSpeed, number> = {
  slow: 0.5,
  medium: 1,
  fast: 2,
};

/** Color Shift: seconds spent blending from one color to the next. */
const SHIFT_SECONDS: Record<LightShowSpeed, number> = {
  slow: 8,
  medium: 4,
  fast: 2,
};

const FADE_SECONDS = 4;
const PULSE_BPM = 120;

// --- color helpers -----------------------------------------------------------

const toRgb = (hex: string): [number, number, number] => {
  const value = parseInt(hex.replace("#", "").padEnd(6, "0").slice(0, 6), 16);
  if (Number.isNaN(value)) return [0, 0, 0];
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const mix = (a: string, b: string, amount: number) => {
  const [r1, g1, b1] = toRgb(a);
  const [r2, g2, b2] = toRgb(b);
  const lerp = (x: number, y: number) => Math.round(x + (y - x) * amount);
  return `rgb(${lerp(r1, r2)},${lerp(g1, g2)},${lerp(b1, b2)})`;
};

const luminance = (hex: string) => {
  const [r, g, b] = toRgb(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

const fill = (ctx: Ctx, w: number, h: number, color: string, alpha = 1) => {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
  ctx.globalAlpha = 1;
};

// --- solid -------------------------------------------------------------------

const renderSolid = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  { color, motion }: SolidLook,
  seed: number,
) => {
  switch (motion) {
    case "fade": {
      const wave = (Math.cos((t / FADE_SECONDS) * 2 * Math.PI) + 1) / 2;
      fill(ctx, w, h, "#000");
      fill(ctx, w, h, color, 0.15 + 0.85 * wave);
      return;
    }
    case "pulse": {
      const hz = Math.min(PULSE_BPM / 60, MAX_FLASH_HZ);
      const wave = (Math.cos(t * hz * 2 * Math.PI) + 1) / 2;
      fill(ctx, w, h, "#000");
      fill(ctx, w, h, color, 0.25 + 0.75 * wave * wave);
      return;
    }
    case "twinkle": {
      // Each phone sparkles at its own moment, so the crowd reads as a starfield.
      const k = ((t + seed * 20) % 6) / 6;
      const sparkle = k < 0.12 ? Math.sin((k / 0.12) * Math.PI) : 0;
      fill(ctx, w, h, "#000");
      fill(ctx, w, h, color, 0.08 + sparkle * 0.92);
      return;
    }
    default:
      fill(ctx, w, h, color);
  }
};

// --- color shift -------------------------------------------------------------

const renderColorShift = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  { colors, speed }: ColorShiftLook,
) => {
  if (colors.length < 2) {
    fill(ctx, w, h, colors[0] || "#000");
    return;
  }
  const seconds = SHIFT_SECONDS[speed];
  const cycle = seconds * colors.length;
  // t can be just under 0 at a look's first frame; keep the index in range.
  const position = (((t % cycle) + cycle) % cycle) / seconds;
  const index = Math.floor(position);
  const progress = position - index;
  // Ease so each color settles for a moment before blending to the next.
  const eased = progress * progress * (3 - 2 * progress);
  fill(
    ctx,
    w,
    h,
    mix(colors[index], colors[(index + 1) % colors.length], eased),
  );
};

// --- patterns ----------------------------------------------------------------

/**
 * A pattern is a still drawing that repeats vertically every `tile(w)` pixels.
 * Shapes may overhang their tile; every tile is drawn, so it stays seamless.
 * `cycles` is the most color changes a vertical line crosses in one tile,
 * which turns PATTERN_HZ into a scroll speed.
 *
 * Shapes, sizes and spacing come from the comps (shapes.ts,
 * confetti.ts). Each pattern draws in comp units, a COMP_WIDTH-wide frame
 * scaled to the screen width, so it looks the same on every phone.
 */
interface TiledPattern {
  tile: (w: number) => number;
  cycles: number;
  draw: (ctx: Ctx, w: number, tile: number, look: PatternLook) => void;
}

/** Pattern tile height in pixels for a tile `height` comp units tall. */
const compTile = (w: number, height: number) => (w / COMP_WIDTH) * height;

/** Runs `draw` in comp units: x and y in a COMP_WIDTH-wide frame. */
const inComp = (ctx: Ctx, w: number, draw: () => void) => {
  const scale = w / COMP_WIDTH;
  ctx.save();
  ctx.scale(scale, scale);
  draw();
  ctx.restore();
};

/** SVG path data as a Path2D, built once on first use. */
const lazyPath = (d: string) => {
  let path: Path2D | undefined;
  return () => (path ??= new Path2D(d));
};

const BOLT = lazyPath(BOLT_PATH);
const STAR = lazyPath(STAR_PATH);
const HEART = lazyPath(HEART_PATH);
const CHEVRON = lazyPath(CHEVRON_PATH);
const WAVES = [WAVE_B, WAVE_A].map((wave) => ({
  ...wave,
  path: lazyPath(wave.path),
}));

const fillPathAt = (ctx: Ctx, path: Path2D, x: number, y: number) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.fill(path);
  ctx.restore();
};

const triangle = (ctx: Ctx, apexX: number, apexY: number, height: number) => {
  // Equilateral; a negative height points it down.
  const half = Math.abs(height) / Math.sqrt(3);
  ctx.beginPath();
  ctx.moveTo(apexX, apexY);
  ctx.lineTo(apexX + half, apexY + height);
  ctx.lineTo(apexX - half, apexY + height);
  ctx.closePath();
  ctx.fill();
};

// Keys and lines repeat every white key; black keys sit on five of every seven
// lines (groups of three and two), like a piano octave. Per the comp, the group
// of three sits a line's width higher than the group of two.
const KEY_PITCH = 115.8;
const KEY_LINE = 8.27;
const BLACK_KEY = { width: 194.4, height: 74.45 };
const BLACK_KEYS = [
  { line: 0, top: -45.5 },
  { line: 1, top: -45.5 },
  { line: 2, top: -45.5 },
  { line: 4, top: -37.2 },
  { line: 5, top: -37.2 },
];

// Each column is a 4-square trail, faint to bright as it falls, then a 4-square
// gap; the columns start 0, 5 and 2 squares into the tile.
const DIGITAL_CELL = COMP_WIDTH / 3;
const DIGITAL_ALPHAS = [0.25, 0.5, 0.75, 1];
const DIGITAL_STARTS = [0, 5, 2];

// The confetti comp is a seamless square tile, rasterized once from its SVG.
// The SVG is large, so apps load it on demand through setConfettiLoader; until
// it's loaded the pattern draws just its background.
const CONFETTI_OFFSET_X = -27.48;
const CONFETTI_RESOLUTION = 1200;
let loadConfettiSvg: (() => Promise<string>) | undefined;
let confettiTile: HTMLCanvasElement | null | undefined;

/**
 * How this app loads the confetti artwork (`@max/common/lightshow/confetti`).
 * Register it once with a dynamic import, e.g.
 * `setConfettiLoader(() => import("@max/common/lightshow/confetti").then((m) => m.CONFETTI_TILE_SVG))`,
 * so the app's bundler keeps the artwork in its own chunk. (A dynamic import
 * here would compile to a plain require and land in every app's main bundle.)
 */
export const setConfettiLoader = (loader: () => Promise<string>) => {
  loadConfettiSvg = loader;
};

const getConfettiTile = () => {
  if (confettiTile !== undefined) return confettiTile;
  if (!loadConfettiSvg) return null;
  confettiTile = null;
  loadConfettiSvg()
    .then((svg) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = CONFETTI_RESOLUTION;
        canvas.height = CONFETTI_RESOLUTION;
        canvas
          .getContext("2d")
          ?.drawImage(image, 0, 0, CONFETTI_RESOLUTION, CONFETTI_RESOLUTION);
        confettiTile = canvas;
      };
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    })
    .catch(() => {
      // Try again on a later frame, e.g. after a dropped connection.
      confettiTile = undefined;
    });
  return null;
};

const PATTERNS: Record<Exclude<PatternId, "radial">, TiledPattern> = {
  keyboard: {
    // One octave of white keys running across the screen.
    tile: (w) => compTile(w, KEY_PITCH * 7),
    cycles: 7,
    draw: (ctx, w, tile, { colors: [fg, bg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        ctx.fillRect(0, 0, COMP_WIDTH, KEY_PITCH * 7);
        ctx.fillStyle = bg;
        for (let i = 0; i < 7; i++) {
          ctx.fillRect(0, i * KEY_PITCH - KEY_LINE / 2, COMP_WIDTH, KEY_LINE);
        }
        for (const { line, top } of BLACK_KEYS) {
          ctx.fillRect(
            0,
            line * KEY_PITCH + top,
            BLACK_KEY.width,
            BLACK_KEY.height,
          );
        }
      }),
  },
  wavelength: {
    // Two centered clusters of bars, like an audio waveform, 471.74 apart.
    tile: (w) => compTile(w, 943.48),
    cycles: 24,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        WAVES.forEach((wave, i) => {
          ctx.save();
          ctx.translate(COMP_WIDTH / 2, 249 + i * 471.74);
          ctx.rotate(-Math.PI / 2);
          ctx.translate(-wave.width / 2, -wave.height / 2);
          ctx.fill(wave.path());
          ctx.restore();
        });
      }),
  },
  stars: {
    // Two columns of large stars running off both edges, the right one lower.
    tile: (w) => compTile(w, 592.87),
    cycles: 4,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        fillPathAt(ctx, STAR(), -66.67, 0);
        fillPathAt(ctx, STAR(), -66.67, 303.42);
        fillPathAt(ctx, STAR(), 191.19, 205.04);
        fillPathAt(ctx, STAR(), 191.19, 508.46);
      }),
  },
  stripes: {
    tile: (w) => compTile(w, 434.41),
    cycles: 1,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        ctx.fillRect(0, 0, COMP_WIDTH, 221.3);
      }),
  },
  chevrons: {
    tile: (w) => compTile(w, 249.1),
    cycles: 1,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        // The comp fills the outline and strokes it 20pt, which carries the
        // arms almost to the screen edges.
        ctx.save();
        ctx.translate(12.83, -28.28);
        ctx.fillStyle = fg;
        ctx.strokeStyle = fg;
        ctx.lineWidth = 20;
        ctx.lineJoin = "miter";
        ctx.fill(CHEVRON());
        ctx.stroke(CHEVRON());
        ctx.restore();
      }),
  },
  digital: {
    // "Falling squares": three columns of fading trails in shades of Color 1.
    tile: (w) => compTile(w, DIGITAL_CELL * 8),
    cycles: 5,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        DIGITAL_STARTS.forEach((start, column) =>
          DIGITAL_ALPHAS.forEach((alpha, i) => {
            ctx.globalAlpha = alpha;
            ctx.fillRect(
              column * DIGITAL_CELL,
              (start + i) * DIGITAL_CELL,
              DIGITAL_CELL + 0.5,
              DIGITAL_CELL + 0.5,
            );
          }),
        );
        ctx.globalAlpha = 1;
      }),
  },
  hearts: {
    // Two columns of pixel hearts running off both edges, the right one lower.
    tile: (w) => compTile(w, 324.97),
    cycles: 2,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        fillPathAt(ctx, HEART(), -71.79, 0);
        fillPathAt(ctx, HEART(), 202.12, 158.39);
      }),
  },
  triangles: {
    // A row of upward triangles, then a row of downward ones offset by half a
    // triangle, so the gaps between them zigzag.
    tile: (w) => compTile(w, 453.11),
    cycles: 2,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        for (let x = 120 - 321.13; x < COMP_WIDTH + 321.13; x += 321.13) {
          triangle(ctx, x, 0, 229.56);
          triangle(ctx, x - 160.57, 318.13 + 229.56, -229.56);
        }
      }),
  },
  lightning: {
    // Bolts on a 155pt square grid, so each overlaps the next row and they
    // interlock diagonally.
    tile: (w) => compTile(w, 155),
    cycles: 2,
    draw: (ctx, w, tile, { colors: [fg] }) =>
      inComp(ctx, w, () => {
        ctx.fillStyle = fg;
        for (let x = -33.5; x < COMP_WIDTH; x += 155) {
          fillPathAt(ctx, BOLT(), x, 0);
        }
      }),
  },
  emoji: {
    // One large centered column; Color 2 only, the glyph brings its own colors.
    tile: (w) => compTile(w, 400),
    cycles: 2,
    draw: (ctx, w, tile, { emoji }) => {
      const text = emoji || "🤘";
      ctx.font = `${w * 0.999}px sans-serif`;
      // Centered by hand: before Safari 27, textAlign "center" draws
      // complex-shaped text (e.g. 🏔️ with its variation selector) starting
      // at x instead of centered on it. measureText is still right there.
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#fff";
      ctx.fillText(text, (w - ctx.measureText(text).width) / 2, tile / 2);
    },
  },
  confetti: {
    // The comp's own colors on Color 2; Color 1 isn't used.
    tile: (w) => compTile(w, CONFETTI_TILE_SIZE),
    cycles: 8,
    draw: (ctx, w) => {
      const image = getConfettiTile();
      if (!image) return;
      inComp(ctx, w, () => {
        ctx.translate(CONFETTI_OFFSET_X, 0);
        ctx.drawImage(image, 0, 0, CONFETTI_TILE_SIZE, CONFETTI_TILE_SIZE);
      });
    },
  },
};

/** Motions each pattern supports; the builder can use this to disable options. */
export const PATTERN_MOTIONS: Record<PatternId, PatternMotion[]> = {
  ...(Object.fromEntries(
    Object.keys(PATTERNS).map((id) => [id, ["none", "scrollUp", "scrollDown"]]),
  ) as Record<Exclude<PatternId, "radial">, PatternMotion[]>),
  radial: ["none", "radiate"],
};

/**
 * Concentric target with a Color 2 center, rings a fifth of the screen wide
 * (the comp's 80pt rings); radiating rings expand from the center.
 */
const renderRadial = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  { colors: [fg, bg], motion, speed }: PatternLook,
) => {
  const ring = w * 0.2;
  const offset =
    motion === "radiate"
      ? ((t * PATTERN_HZ[speed]) % 1) * 2 * ring
      : ring * 0.5;
  const count = Math.ceil(Math.hypot(w, h) / 2 / ring) + 2;
  fill(ctx, w, h, bg);
  for (let k = count; k >= 0; k--) {
    const radius = k * ring + offset;
    if (radius <= 0) continue;
    ctx.fillStyle = k % 2 ? fg : bg;
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, radius, 0, 2 * Math.PI);
    ctx.fill();
  }
};

const renderPattern = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  look: PatternLook,
) => {
  const motion = PATTERN_MOTIONS[look.pattern].includes(look.motion)
    ? look.motion
    : "none";

  if (look.pattern === "radial") {
    renderRadial(ctx, w, h, t, { ...look, motion });
    return;
  }

  const pattern = PATTERNS[look.pattern];
  const tile = pattern.tile(w);
  let shift = 0;
  if (motion === "scrollUp" || motion === "scrollDown") {
    const tilesPerSecond = PATTERN_HZ[look.speed] / pattern.cycles;
    const travelled = ((t * tilesPerSecond) % 1) * tile;
    shift = motion === "scrollDown" ? travelled : -travelled;
  }

  fill(ctx, w, h, look.colors[1]);
  // Start two tiles above the screen so overhanging shapes stay continuous.
  const first = (((shift % tile) + tile) % tile) - 2 * tile;
  for (let y = first; y < h + tile; y += tile) {
    ctx.save();
    ctx.translate(0, y);
    pattern.draw(ctx, w, tile, look);
    ctx.restore();
  }
};

// --- public API --------------------------------------------------------------

export const renderLook = (
  ctx: Ctx,
  w: number,
  h: number,
  /** Seconds since the look started. */
  t: number,
  look: LightShowLook,
  /** Stable random number in [0, 1) per phone, for per-phone variation. */
  seed: number,
) => {
  switch (look.type) {
    case "solid":
      return renderSolid(ctx, w, h, t, look, seed);
    case "colorShift":
      return renderColorShift(ctx, w, h, t, look);
    case "pattern":
      return renderPattern(ctx, w, h, t, look);
    case "custom":
      return CUSTOM_LOOKS[look.customLookId]?.render(ctx, w, h, t, seed);
    case "animation":
      return renderAnimation(ctx, w, h, t, look, seed);
  }
};

/** A custom look's main colors, for swatches. */
export const getCustomLookColors = (customLookId: CustomLookId) =>
  CUSTOM_LOOKS[customLookId]?.colors ?? [];

/**
 * Starts loading any artwork `look` loads on demand (confetti, the cassette,
 * an animation's file), so it's ready by the time the look goes live.
 */
export const preloadLook = (look: LightShowLook) => {
  if (look.type === "pattern" && look.pattern === "confetti") {
    getConfettiTile();
  } else if (look.type === "custom") {
    CUSTOM_LOOKS[look.customLookId]?.load?.();
  } else if (look.type === "animation") {
    preloadAnimation(look);
  }
};

/** Whether `look`'s on-demand artwork, if any, has loaded. */
export const isLookReady = (look: LightShowLook) => {
  if (look.type === "pattern" && look.pattern === "confetti") {
    return !!getConfettiTile();
  }
  if (look.type === "custom") {
    return CUSTOM_LOOKS[look.customLookId]?.loaded?.() ?? true;
  }
  if (look.type === "animation") return isAnimationReady(look);
  return true;
};

/**
 * When to draw a still of `look` (a thumbnail): the start, unless it's a
 * custom look that shows little there, like a twinkle that starts on black,
 * or an animation with a "still" marker.
 */
export const getStillTime = (look: LightShowLook, seed: number) => {
  if (look.type === "custom") {
    return CUSTOM_LOOKS[look.customLookId]?.stillTime?.(seed) ?? 0;
  }
  if (look.type === "animation") return getAnimationStillTime(look, seed);
  return 0;
};

/**
 * Per the comps, the exit control on a pattern is outlined with the lighter of
 * its two colors; on a color it's just the 75% black fill.
 */
export const getExitStroke = (look: LightShowLook) => {
  // Emoji and confetti bring their own colors, so Color 1 isn't on screen.
  if (
    look.type !== "pattern" ||
    look.pattern === "emoji" ||
    look.pattern === "confetti"
  ) {
    return undefined;
  }
  const [a, b] = look.colors;
  return luminance(a) >= luminance(b) ? a : b;
};
